import { BigNum } from '@drift-labs/sdk';
import { COMMON_UI_UTILS } from '@drift/common';
import { LiquidityDisplayType } from './OrderbookTypes';
import { ORDERBOOK_DISPLAY_TYPE } from './useOrderbookDisplayPreference';
import { getColorVar } from 'src/utils/theme/colorVars';
import { getEffectivePriceDecimals } from 'src/constants/marketDecimalConfig';

/**
 * To support hover style on the siblings of the hovered row, we need to reverse the flex direction in some cases.
 *
 * Explanation:
 * - The SIBLING-HOVER CSS style can only apply to FOLLOWING elements (css can't go in the other direction). This is why we may want to flex-reverse the items when the hover style should be applied to the PRECEEDING sibling.
 *
 * @param side
 * @param displayType
 */
export const shouldBeFlexReversed = (
	side: 'buy' | 'sell',
	displayType: ORDERBOOK_DISPLAY_TYPE
) => {
	switch (displayType) {
		case ORDERBOOK_DISPLAY_TYPE.HORIZONTAL:
			// When we're in horizontal mode, both sides go in the same direction (TOP to BOTTOM)
			// We want the PRECEDING siblings to be highlighted, so we always flex-reverse the items in horizontal mode
			return true;
		case ORDERBOOK_DISPLAY_TYPE.VERTICAL:
			// When we're in vertical mode, the SELL SIDE is at the TOP, the BUY SIDE is at the BOTTOM
			// On the TOP, the FOLLOWING siblings should be highlighted so there is no need to flex reverse the items
			// On the BOTTOM, the PREVIOUS siblings should be highlighted so we need to flex reverse the items
			return side === 'buy';
		default: {
			const _exhaustiveCheck: never = displayType;
			throw new Error(`Invalid display type: ${_exhaustiveCheck}`);
		}
	}
};

export const getLiquidityDisplayBgStyle = (
	liquidityType: LiquidityDisplayType,
	side: 'buy' | 'sell'
): React.CSSProperties => {
	const isGradient = liquidityType === 'indicative';
	const isBuy = side === 'buy';

	const solidColor = isBuy
		? getColorVar('positive-green-secondary-bg')
		: getColorVar('negative-red-secondary-bg');

	if (isGradient) {
		return {
			background: `repeating-linear-gradient(55deg, ${solidColor} 0 5px, transparent 5px 7px)`,
			opacity: 0.12,
		};
	}

	return {
		background: solidColor,
		opacity: 1,
	};
};

/**
 * To support hover style on the siblings of the hovered row, we sometimes need to reverse the array of rows.
 *
 * Explanation:
 * - First see shouldBeFlexReversed for the explanation of why we need to reverse the flex direction in some cases
 * - We basically only need to reverse the array if we expect the flex-reverse to put the rows in the wrong order
 *
 * @param side
 * @param displayType
 */
export const shouldBeArrayReversed = (
	side: 'buy' | 'sell',
	displayType: ORDERBOOK_DISPLAY_TYPE
) => {
	switch (displayType) {
		case ORDERBOOK_DISPLAY_TYPE.HORIZONTAL:
			// The L2 rows are already in the correct order for horizontal view (price descending for BUY, price ascending for SELL)
			// We don't need to array-reverse the array UNLESS we're applying flex-reverse to the rows
			return shouldBeFlexReversed(side, displayType);
		case ORDERBOOK_DISPLAY_TYPE.VERTICAL:
			// When we're in vertical mode, the SELL SIDE is at the TOP, the BUY SIDE is at the BOTTOM
			// The L2 rows are by default sorted in the "expected" order (top-bid/top-ask=>index 0 .. aka price descending for BUY, price ascending for SELL)
			// SELL SIDE:
			// The SELL side rows are going to be in the wrong order by default (they are on the TOP so they should be in price descending order, the top ask should be at the LAST index)
			// For SELL side : We only array-reverse if flex-reverse is NOT applied (we expect flex-reverse to NOT be applied, but this is a more generic way to handle it)
			// BUY SIDE:
			// The BUY side rows are going to be in the correct order by default
			// For BUY side : We only array-reverse if flex-reverse is applied (to negate the flex-reverse, because we know they should be in the correct order by default)
			if (side === 'sell') return !shouldBeFlexReversed(side, displayType);
			if (side === 'buy') return shouldBeFlexReversed(side, displayType);
			break;
		default: {
			const _exhaustiveCheck: never = displayType;
			throw new Error(`Invalid display type: ${_exhaustiveCheck}`);
		}
	}
};

interface GetFormattedSpreadQuoteParams {
	baseAssetSymbol?: string;
	tickSizeDecimalPlaces: number;
	spreadQuote: BigNum;
}

export function getFormattedSpreadQuote({
	baseAssetSymbol,
	tickSizeDecimalPlaces,
	spreadQuote,
}: GetFormattedSpreadQuoteParams) {
	const effectiveDecimalPlaces = getEffectivePriceDecimals(
		baseAssetSymbol ?? '',
		tickSizeDecimalPlaces
	);

	return COMMON_UI_UTILS.trimTrailingZeros(
		spreadQuote.toFixed(effectiveDecimalPlaces),
		0
	);
}
